import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;

const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function getErrorMessage(error) {
  const isProd = !import.meta.env.DEV;
  if (error?.code === 'ECONNABORTED') {
    return isProd
      ? 'The server is taking too long to respond. Please try again in a moment.'
      : `Backend did not respond in time. Start the API: cd backend && .\\start-api.bat (${API_URL})`;
  }
  if (error?.code === 'ERR_NETWORK' || !error?.response) {
    return isProd
      ? 'Cannot reach the server. Check your connection or contact support.'
      : `Cannot reach the backend at ${API_URL}. Start the API with .\\start-api.bat in the backend folder.`;
  }
  return (
    error?.response?.data?.detail
    || error?.message
    || 'Request failed'
  );
}

export const authAPI = {
  async login(password) {
    const trimmed = (password || '').trim();
    if (!trimmed) {
      throw new Error('Password is required');
    }
    const response = await api.post('/api/v1/auth/login', { password: trimmed });
    return response.data;
  },

  async checkHealth() {
    const response = await api.get('/api/v1/health');
    return response.data;
  },
};

export const centerAPI = {
  async listCenters({ activeOnly = false } = {}) {
    const response = await api.get('/api/v1/centers', {
      params: { active_only: activeOnly },
    });
    const data = response.data ?? {};
    return {
      data: {
        ...data,
        items: Array.isArray(data.items) ? data.items : [],
      },
    };
  },

  async getCenter(centerId) {
    const response = await api.get(`/api/v1/centers/${centerId}`);
    return { data: response.data };
  },

  async createCenter(payload) {
    const response = await api.post('/api/v1/centers', payload);
    return { data: response.data };
  },

  async updateCenter(centerId, payload) {
    const response = await api.patch(`/api/v1/centers/${centerId}`, payload);
    return { data: response.data };
  },

  async getCenterUsers(centerId, { page = 0, pageSize = 50 } = {}) {
    const response = await api.get(`/api/v1/centers/${centerId}/users`, {
      params: { skip: page * pageSize, limit: pageSize },
    });
    return { data: response.data };
  },
};

export const paymentAPI = {
  async getPayments({ page = 0, pageSize = 10, status = null, centerId = null } = {}) {
    const params = {
      skip: page * pageSize,
      limit: pageSize,
    };
    if (status) {
      params.status = status;
    }
    if (centerId) {
      params.center_id = centerId;
    }
    const response = await api.get('/api/v1/payments', { params });
    const data = response.data ?? {};
    return {
      data: {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total ?? 0,
        page: (data.page ?? 1) - 1,
        pageSize: data.page_size ?? pageSize,
      },
    };
  },

  async getPayment(paymentId) {
    const response = await api.get(`/api/v1/payments/${paymentId}`);
    return { data: response.data };
  },

  async updatePaymentStatus(paymentId, status, payload = {}) {
    const response = await api.patch(`/api/v1/payments/${paymentId}/status`, {
      status,
      rejection_reason: payload.rejection_reason ?? null,
      info_requested: payload.info_requested ?? null,
      notes: payload.notes ?? null,
    });
    return { data: response.data };
  },

  async getStats() {
    const response = await api.get('/api/v1/payments/stats/overview');
    return { data: response.data };
  },
};

export const registrationAPI = {
  async getRegistrations({ page = 0, pageSize = 10, status = null, centerId = null } = {}) {
    const params = {
      skip: page * pageSize,
      limit: pageSize,
    };
    if (status) {
      params.status = status;
    }
    if (centerId) {
      params.center_id = centerId;
    }
    const response = await api.get('/api/v1/registrations', { params });
    const data = response.data ?? {};
    return {
      data: {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total ?? 0,
        page: (data.page ?? 1) - 1,
        pageSize: data.page_size ?? pageSize,
      },
    };
  },

  async getRegistration(userId) {
    const response = await api.get(`/api/v1/registrations/${userId}`);
    return { data: response.data };
  },

  async approveRegistration(registrationId, { courseId, assignedFee = null }) {
    const response = await api.post(`/api/v1/registrations/${registrationId}/approve`, {
      course_id: courseId,
      assigned_fee: assignedFee,
    });
    return { data: response.data };
  },

  async rejectRegistration(registrationId, rejectionReason) {
    const response = await api.post(`/api/v1/registrations/${registrationId}/reject`, {
      rejection_reason: rejectionReason,
    });
    return { data: response.data };
  },

  async updateStatus(registrationId, status, rejectionReason = null) {
    const response = await api.patch(`/api/v1/registrations/${registrationId}/status`, {
      status,
      rejection_reason: rejectionReason,
    });
    return { data: response.data };
  },

  async getStats() {
    const response = await api.get('/api/v1/registrations/stats/overview');
    return { data: response.data };
  },

  async getRegisteredRoster({ centerId = null, courseId = null, term = null } = {}) {
    const params = {};
    if (centerId) params.center_id = centerId;
    if (courseId) params.course_id = courseId;
    if (term) params.term = term;
    const response = await api.get('/api/v1/registrations/registered/roster', { params });
    return { data: response.data };
  },

  async getRosterTerms() {
    const response = await api.get('/api/v1/registrations/registered/roster/terms');
    return { data: response.data };
  },

  getProofUrl(userId) {
    return `${API_URL}/api/v1/registrations/${userId}/proof`;
  },
};

export const receiptAPI = {
  async getReceipt(paymentId) {
    const response = await api.get(`/api/v1/receipts/${paymentId}`);
    return { data: response.data };
  },

  async downloadReceipt(receiptId) {
    const response = await api.get(`/api/v1/receipts/${receiptId}/download`, {
      responseType: 'blob',
    });
    return { data: response.data };
  },
};

export const courseAPI = {
  async listCourses({ centerId = null, activeOnly = false } = {}) {
    const params = { active_only: activeOnly };
    if (centerId) params.center_id = centerId;
    const response = await api.get('/api/v1/courses', { params });
    return { data: response.data };
  },

  async createCourse(payload) {
    const response = await api.post('/api/v1/courses', payload);
    return { data: response.data };
  },

  async updateCourse(courseId, payload) {
    const response = await api.patch(`/api/v1/courses/${courseId}`, payload);
    return { data: response.data };
  },
};

export { api, getErrorMessage, API_URL };
